import ExpiredLink from '@/components/expired-link';
import FormattedMessage from '@/components/formatted-message';
import Logo from '@/components/logo';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/ui/input-password';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { userEmailChangeVerifyMutationFn } from '@/lib/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ChevronLeft, Loader } from 'lucide-react';
import { useState } from 'react';
import {
    Link,
    useNavigate,
    useOutletContext,
    useSearchParams,
} from 'react-router-dom';

export default function VerifyEmailChange() {
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = params.get('token');

    const exp = Number(params.get('exp'));
    const now = Date.now();
    const isExpired = token && exp && exp <= now;

    const queryClient = useQueryClient();
    const { userId } = useOutletContext<{ userId: string }>();

    const { mutate, isPending } = useMutation({
        mutationFn: userEmailChangeVerifyMutationFn,
    });

    const handleSubmit = (e: { preventDefault: () => void }) => {
        const jwtRegex =
            /^([a-zA-Z0-9_=]+)\.([a-zA-Z0-9_=]+)\.([a-zA-Z0-9_\-\\+\\/=]*)/gi;
        e.preventDefault();
        if (!token || !jwtRegex.test(token as string) || !password) {
            toast({
                title: 'Error',
                description: password
                    ? 'Verification token not found'
                    : 'Password is required.',
                variant: 'destructive',
            });
            return;
        }
        mutate(
            { token, password, ...(userId && { clientUserId: userId }) },

            {
                onSuccess: (response) => {
                    toast({
                        title: 'Success',
                        description: (
                            <FormattedMessage message={response.data.message} />
                        ),
                        variant: 'default',
                        duration: 15000,
                    });
                    queryClient.invalidateQueries({
                        queryKey: ['authUser'],
                        refetchType: 'all',
                    });
                    navigate(`/${userId ? 'settings' : 'login'}`, {
                        replace: true,
                    });
                },
                onError: (error) => {
                    setError(error.message);
                    toast({
                        title: 'Error',
                        description: (
                            <FormattedMessage message={error.message} />
                        ),
                        variant: 'destructive',
                    });
                },
            },
        );
    };
    return (
        <main className='max-w-lg min-h-[590px] h-full flex items-center justify-center mx-auto'>
            {isExpired ? (
                <ExpiredLink
                    title='Invalid or expired email change link'
                    message='You can make a new email change request'
                    url={userId ? '/settings' : '/login'}
                    btnTitle={userId ? 'Go to settings' : 'Go to login'}
                />
            ) : (
                <div className='size-full flex flex-col p-5 rounded-md'>
                    <Logo size='100px' />

                    <h1
                        className='text-xl tracking-[-0.16px] dark:text-[#fcfdffef] font-bold mb-2 mt-8
        text-center'>
                        Email Change Verification
                    </h1>
                    <p className='mb-6 text-center text-[15px] dark:text-[#f1f7feb5] font-normal'>
                        To confirm your email change request, please enter your
                        password and click the button below.
                    </p>
                    <form
                        onSubmit={handleSubmit}
                        className='w-full flex flex-col items-center space-y-4'>
                        <div className='space-y-2 w-full'>
                            <Label htmlFor='password'>Password</Label>
                            <PasswordInput
                                id='password'
                                placeholder='Enter your password'
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={isPending}
                                autoComplete='current-password'
                            />
                        </div>

                        {error && (
                            <Alert
                                variant='destructive'
                                className='h-fit py-4 flex items-center [&>svg]:sticky'>
                                <AlertCircle className='size-7' />
                                <AlertDescription className='!translate-y-0'>
                                    <FormattedMessage message={error} />
                                </AlertDescription>
                            </Alert>
                        )}
                        <Button
                            disabled={isPending}
                            type='submit'
                            className='w-full mx-auto text-[15px] h-[40px] text-white font-semibold'>
                            {isPending && <Loader className='animate-spin' />}
                            Verify Email
                        </Button>
                    </form>

                    <p className='mt-6 text-center text-sm text-muted-foreground dark:text-[#f1f7feb5] font-normal'>
                        If you have any issue confirming your email change
                        request please{' '}
                        <Link
                            target='_blank'
                            rel='noreferrer'
                            className='outline-none transition duration-150 ease-in-out 
            focus-visible:ring-2 text-primary hover:underline focus-visible:ring-primary font-semibold underline-offset-4'
                            to='//x.com/_isCoding'>
                            Contact us
                        </Link>
                        .
                    </p>
                    {userId && (
                        <Link
                            to={'/settings'}
                            className='flex justify-center pt-4'>
                            <Button
                                className='h-fit flex flex-col justify-center'
                                variant={'link'}>
                                <ChevronLeft className='!size-8 border-2 rounded-full' />
                                Go to settings
                            </Button>
                        </Link>
                    )}
                </div>
            )}
        </main>
    );
}
